import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { agentSignup } from '../api/auth';
import PageLink from '../components/PageLink';
import logoMark from '../assets/logo-mark.png';

interface FormState {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreed: boolean;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  agreed: false,
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

const validate = (f: FormState): FieldErrors => {
  const errs: FieldErrors = {};
  if (!f.name.trim()) errs.name = 'Please enter your full name.';
  if (!f.email.trim()) errs.email = 'Email is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email))
    errs.email = 'Enter a valid email address.';
  if (!f.password) errs.password = 'Password is required.';
  else if (f.password.length < 6)
    errs.password = 'Password must be at least 6 characters.';
  if (f.password !== f.confirmPassword)
    errs.confirmPassword = 'Passwords do not match.';
  if (!f.agreed)
    errs.agreed = 'You must agree to the terms to continue.';
  return errs;
};

const AgentRegister: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ name: string; email: string } | null>(
    null
  );

  const setField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setSubmitting(true);
    try {
      await agentSignup({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        confirmPassword: form.confirmPassword,
      });
      setSuccess({ name: form.name.trim(), email: form.email.trim().toLowerCase() });
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let msg: string;
      if (data?.error) {
        msg = Array.isArray(data.error) ? data.error.join(', ') : data.error;
      } else if (status === 409) {
        msg = 'An account with that email already exists.';
      } else if (err?.code === 'ERR_NETWORK') {
        msg = 'Cannot reach the server. Make sure the backend is running on port 3000.';
      } else if (
        err?.message &&
        !err.message.toLowerCase().includes('status code')
      ) {
        msg = err.message;
      } else {
        msg = 'Registration failed. Please try again.';
      }
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Success screen (pending approval) ─────────────────────────────────
  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-10"
        style={{ background: '#E8E6E6' }}
      >
        <div className="w-full max-w-md">
          <div className="card p-6 sm:p-8 text-center animate-fadeIn">
            <div
              className="rounded-full mx-auto flex items-center justify-center mb-5"
              style={{
                width: 72,
                height: 72,
                background: 'var(--brand-bg)',
              }}
            >
              <Icon name="check-circle" size={32} color="var(--brand)" />
            </div>
            <h1 className="text-[22px] font-extrabold text-ink mb-2">
              Application received
            </h1>
            <p
              className="text-sm text-g600 leading-relaxed mb-6"
              style={{ maxWidth: 340, margin: '0 auto 24px' }}
            >
              Thanks <strong className="text-ink">{success.name}</strong>!
              Your merchant account is pending admin approval. We will notify you
              at{' '}
              <strong className="text-ink">{success.email}</strong> once it has
              been reviewed.
            </p>

            <div
              className="rounded-[10px] px-4 py-3 mb-6 text-left"
              style={{
                background: 'var(--blue-bg)',
                border: '1px solid rgba(37,99,235,.12)',
              }}
            >
              <div className="flex items-start gap-2">
                <Icon name="bell" size={16} color="var(--blue)" />
                <div className="text-[13px] leading-relaxed" style={{ color: 'var(--blue)' }}>
                  <strong>What happens next?</strong>
                  <br />
                  An admin will review your application. You won't be able to
                  sign in until your account is approved.
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary w-full justify-center"
              style={{ paddingTop: 12, paddingBottom: 12 }}
              onClick={() => navigate('/agent/login')}
            >
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Registration form ─────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: '#E8E6E6' }}
    >
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img
            src={logoMark}
            alt="Rentagade"
            className="rounded-full object-contain flex-shrink-0"
            style={{ width: 48, height: 48 }}
          />
          <div className="text-left">
            <div className="text-xl font-extrabold text-ink tracking-tight leading-tight">
              Rentagade
            </div>
            <div
              className="text-[11px] font-semibold uppercase text-g400"
              style={{ letterSpacing: '1.2px' }}
            >
              Merchant Portal
            </div>
          </div>
        </div>

        <div className="card p-6 sm:p-8 animate-fadeIn">
          <h1 className="text-[22px] font-extrabold text-ink mb-1">
            Become a merchant
          </h1>
          <p className="text-sm text-g500 mb-6">
            Create your account. An admin will review and approve it.
          </p>

          {serverError && (
            <div
              role="alert"
              className="mb-4 rounded-[10px] px-4 py-3 text-sm font-medium flex items-start gap-2"
              style={{
                background: 'var(--red-bg)',
                color: 'var(--red)',
                border: '1px solid rgba(220,38,38,.12)',
              }}
            >
              <Icon name="x-circle" size={16} color="var(--red)" />
              <span>{serverError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Name */}
            <div className="mb-4">
              <label className="form-label" htmlFor="agent-name">
                Full name
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Icon name="user" size={16} color="var(--g400)" />
                </span>
                <input
                  id="agent-name"
                  type="text"
                  autoComplete="name"
                  className="form-input"
                  style={{
                    paddingLeft: 38,
                    borderColor: errors.name ? 'var(--red)' : undefined,
                  }}
                  placeholder="Jane Doe"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  disabled={submitting}
                />
              </div>
              {errors.name && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.name}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="form-label" htmlFor="agent-reg-email">
                Email address
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Icon name="mail" size={16} color="var(--g400)" />
                </span>
                <input
                  id="agent-reg-email"
                  type="email"
                  autoComplete="email"
                  className="form-input"
                  style={{
                    paddingLeft: 38,
                    borderColor: errors.email ? 'var(--red)' : undefined,
                  }}
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  disabled={submitting}
                />
              </div>
              {errors.email && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.email}
                </div>
              )}
            </div>

            {/* Password */}
            <div className="mb-4">
              <label className="form-label" htmlFor="agent-reg-password">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Icon name="lock" size={16} color="var(--g400)" />
                </span>
                <input
                  id="agent-reg-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="form-input"
                  style={{
                    paddingLeft: 38,
                    paddingRight: 42,
                    borderColor: errors.password ? 'var(--red)' : undefined,
                  }}
                  placeholder="At least 6 characters"
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md flex items-center justify-center hover:bg-g50"
                  style={{ width: 36, height: 36 }}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    name={showPassword ? 'eye-off' : 'eye'}
                    size={16}
                    color="var(--g500)"
                  />
                </button>
              </div>
              {errors.password && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.password}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="mb-4">
              <label className="form-label" htmlFor="agent-reg-confirm">
                Confirm password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Icon name="lock" size={16} color="var(--g400)" />
                </span>
                <input
                  id="agent-reg-confirm"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="form-input"
                  style={{
                    paddingLeft: 38,
                    borderColor: errors.confirmPassword
                      ? 'var(--red)'
                      : undefined,
                  }}
                  placeholder="Re-enter your password"
                  value={form.confirmPassword}
                  onChange={(e) =>
                    setField('confirmPassword', e.target.value)
                  }
                  disabled={submitting}
                />
              </div>
              {errors.confirmPassword && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.confirmPassword}
                </div>
              )}
            </div>

            {/* Terms */}
            <div className="mb-5">
              <label className="flex items-start gap-2 text-sm text-g600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.agreed}
                  onChange={(e) => setField('agreed', e.target.checked)}
                  style={{ accentColor: 'var(--brand)' }}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                />
                <span>
                  I agree to the{' '}
                  <PageLink slug="terms-of-service" target="_blank" className="text-brand font-semibold">
                    Terms of Service
                  </PageLink>{' '}
                  and{' '}
                  <PageLink slug="privacy-policy" target="_blank" className="text-brand font-semibold">
                    Privacy Policy
                  </PageLink>.
                </span>
              </label>
              {errors.agreed && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.agreed}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full justify-center"
              style={{ paddingTop: 12, paddingBottom: 12 }}
            >
              {submitting ? (
                'Creating account...'
              ) : (
                <>
                  Create account{' '}
                  <Icon name="chevron-right" size={16} color="#fff" />
                </>
              )}
            </button>
          </form>

          <div
            className="text-center mt-6 pt-6 text-sm text-g500"
            style={{ borderTop: '1px solid var(--g100)' }}
          >
            Already have an account?{' '}
            <Link
              to="/agent/login"
              className="font-semibold text-brand hover:underline"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentRegister;
